import { Avatar, cn, User } from "@nextui-org/react";
import { useMotionValueEvent, useScroll } from "framer-motion";
import { LogOut, User2, UserIcon, Menu, X } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-scroll";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Logo } from "../../components/ui/logo";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "../../components/ui/sheet";

export const MobileNavbar = ({ navItems }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("");
  const [showBackground, setShowBackground] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(window.isLogin || false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSetActive = (to) => {
    setActiveLink(to);
  };
  const handleSetInactive = () => {
    setActiveLink("");
  };

  useMotionValueEvent(scrollY, "change", (value) => {
    if (value > 100) {
      setShowBackground(true);
    } else {
      setShowBackground(false);
    }
  });

  const handleLinkClick = (item) => {
    if (window.isLocalhost)
      navigate("/wordpress/new-home/", {
        state: { scrollTo: item.link, replace: true },
      });
    else
      navigate("/", {
        state: { scrollTo: item.link, replace: true },
      });
    setOpen(false);
  };

  const logOut = async () => {
    try {
      const response = await fetch(
        window.siteUrl + "/wp-json/whizmanage/v1/log_out",
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (response.ok) {
        window.location.reload();
      } else {
        console.error("Logout failed:", response.statusText);
      }
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  return (
    <div
      className={cn(
        "flex justify-between bg-transparent items-center w-full rounded-md px-2.5 py-1.5 transition duration-200 dark",
        showBackground &&
          " bg-neutral-900  shadow-[0px_-2px_0px_0px_var(--neutral-800),0px_2px_0px_0px_var(--neutral-800)]"
      )}
    >
      <Logo />
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger aria-label="Open navigation menu">
          <Menu
            className="text-white h-6 w-6"
            aria-hidden="true"
          />
        </SheetTrigger>
        <SheetContent className="!w-full bg-gradient-to-tl from-fuchsia-950 via-gray-950 to-slate-950 z-50 flex flex-col items-start justify-start space-y-10 !p-0 !pt-6 text-xl text-zinc-600 transition duration-200 hover:text-zinc-800 border-none">
          <div className="flex items-center justify-between w-full px-5">
            <Logo />
            <div className="flex items-center space-x-2">
              <button
                aria-label="Close navigation menu"
                onClick={() => setOpen(!open)}
                className="p-1"
              >
                <X
                  className="h-8 w-8 text-white"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
          <SheetTitle className="!h-0 !p-0 !m-0"></SheetTitle>
          <div className="h-full w-full flex flex-col justify-between">
            <div className="flex flex-col items-start justify-start gap-[14px] px-8">
              {navItems.map((navItem, idx) => (
                <div key={`link=${idx}`}>
                  {(window.isLocalhost &&
                    location.pathname === "/wordpress/new-home/") ||
                  location.pathname === "/new-home/" || location.pathname === "/" ? (
                    <Link
                      activeClass="active"
                      spy={true}
                      to={navItem.link}
                      smooth="easeInOutQuint"
                      duration={1000}
                      delay={200}
                      onSetActive={handleSetActive}
                      onSetInactive={handleSetInactive}
                      offset={-20}
                      onClick={() => setOpen(false)}
                    >
                      <span
                        className="block text-[26px] text-white"
                        onClick={() => setOpen(false)}
                      >
                        {navItem.title}
                      </span>
                    </Link>
                  ) : (
                    <div onClick={() => handleLinkClick(navItem)}>
                      <span className="block text-[26px] text-white">
                        {navItem.title}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex flex-row w-full items-start gap-2.5 px-8 py-4">
              <div className="flex space-x-2 items-center w-full">
                {(window.isLocalhost &&
                  location.pathname != "/wordpress/new-login/") ||
                location.pathname != "/new-login/" ? (
                  !isLoggedIn ? (
                    <a
                      href={window.siteUrl + "/new-login"}
                      className="bg-transparent w-full no-underline group cursor-pointer relative shadow-2xl shadow-zinc-900 rounded-full p-px text-xs font-semibold leading-6 !text-white hover:!text-white inline-block"
                      onClick={() => setOpen(!open)}
                    >
                      <span className="absolute inset-0 overflow-hidden rounded-full">
                        <span className="absolute inset-0 rounded-full bg-[image:radial-gradient(75%_100%_at_50%_0%,rgba(162,28,175,0.6)_0%,rgba(162,28,175,0)_75%)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                      </span>
                      <div className="relative text-base flex items-center justify-center z-10 rounded-full bg-neutral-950/60 py-2 px-4 ring-1 ring-white/10">
                        <span>Login</span>
                        <svg
                          fill="none"
                          height="24"
                          viewBox="0 0 24 24"
                          width="24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.75 8.75L14.25 12L10.75 15.25"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1.5"
                          />
                        </svg>
                      </div>
                      <span className="absolute -bottom-0 left-[1.125rem] h-px w-[calc(100%-2.25rem)] bg-gradient-to-r from-fuchsia-400/0 via-fuchsia-400/90 to-fuchsia-400/0 transition-opacity duration-500 group-hover:opacity-40" />
                    </a>
                  ) : (
                    <div className="!flex h-full items-center !gap-4 pb-4 dark">
                      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                        <DropdownMenuTrigger asChild>
                          <div className="!flex items-center !gap-4">
                            <Avatar
                              // className="cursor-pointer"
                              isPressable
                              isFocusable
                              showFallback
                              name={window.profileName}
                              fallback={
                                <UserIcon
                                  className="w-6 h-6 text-white"
                                  fill="currentColor"
                                />
                              }
                              isBordered
                              radius="lg"
                              src={window.profileImg}
                            />
                            <p className="text-lg text-neutral-300">
                              Hi, {window.profileName}
                            </p>
                          </div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="!w-full m-2">
                          <DropdownMenuLabel>
                            <User
                              name={window.profileName}
                              avatarProps={{
                                name: window.profileName,
                                showFallback: true,
                                src: window.profileImg,
                              }}
                            />
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator />

                          <DropdownMenuGroup>
                            <DropdownMenuItem
                              onClick={() => {
                                window.location.href =
                                  window.siteUrl + "/my-account";
                              }}
                            >
                              <User2 className="mr-2 h-4 w-4" />
                              <span>My account</span>
                            </DropdownMenuItem>
                          </DropdownMenuGroup>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={logOut}>
                            <LogOut className="mr-2 h-4 w-4" />
                            <span>Log out</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
